import React, {useEffect} from "react"
import ReactDOM from "react-dom/client"
import {I18nextProvider, useTranslation} from "react-i18next"

import {createTheme, ThemeProvider} from "@mui/material/styles"
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment"
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider"

import "./main.css"
import "./styles/tableWrapper.css"
import "./styles/iconStyle.css"
import "./styles/buttonStyle.css"
import "./assets/fonts/FontsFree-Net-Poppins-Light.ttf"
import "moment/dist/locale/fr"
import "moment/dist/locale/en-gb"
import {ApolloProvider} from "@apollo/client"
import moment from "moment"

import KeycloakProvider from "@auth/providers/KeycloakProvider"

import apolloClient, {cache} from "./apolloClient"
import App from "./App"
import DocoonProvider from "./context/DocoonProvider"
import ErrorBoundary from "./utils/ErrorBoundary"
import i18n from "./utils/i18n"
import {reloadOnStaleChunk} from "./utils/reloadOnStaleChunk"

// Installed before anything is rendered: a tab left open across a deployment holds
// an index.html pointing at chunks the server has since replaced.
reloadOnStaleChunk()

const resolveMomentLocale = (lang: string | undefined): string =>
    (lang || "fr").toLowerCase().startsWith("en") ? "en-gb" : "fr"

// Activate the moment locale eagerly so month/year names use the chosen language
// from first render (AdapterMoment's adapterLocale alone does not switch moment globally).
moment.locale(resolveMomentLocale(i18n.language))

function LocalizedDatePickerProvider({children}: Readonly<{children: React.ReactNode}>) {
    const {i18n: i18nInstance} = useTranslation()
    const locale = resolveMomentLocale(i18nInstance.language)
    useEffect(() => {
        moment.locale(locale)
    }, [locale])
    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
            {children}
        </LocalizationProvider>
    )
}

const root = ReactDOM.createRoot(document.getElementById("root"))

const theme = createTheme({
    palette: {
        primary: {
            main: "#307ade"
        },
        secondary: {
            main: "#da4fa3"
        }
    }
})

fetch(globalThis.location.origin + "/settings.json")
    .then((response) => {
        if (!response.ok) {
            throw new Error(`settings.json answered ${response.status}`)
        }

        return response.json()
    })
    .then((settings) => {
        localStorage.setItem("appSettings", JSON.stringify(settings))
        // ErrorBoundary wraps everything, Keycloak included. Nested any deeper it
        // could not catch what the provider itself throws, and an unhandled error
        // there unmounts the root — a white page with nothing on screen to explain it.
        root.render(
            <ErrorBoundary>
                <KeycloakProvider keycloakUrl={settings.KEYCLOAK_URL}>
                    <ApolloProvider client={apolloClient}>
                        <I18nextProvider i18n={i18n}>
                            <DocoonProvider cache={cache}>
                                <ThemeProvider theme={theme}>
                                    <LocalizedDatePickerProvider>
                                        <App />
                                    </LocalizedDatePickerProvider>
                                </ThemeProvider>
                            </DocoonProvider>
                        </I18nextProvider>
                    </ApolloProvider>
                </KeycloakProvider>
            </ErrorBoundary>
        )
    })
    // Nothing is rendered outside the promise, so a failure here used to leave the
    // page blank and silent. i18n may be part of what failed, hence the plain text.
    .catch((error) => {
        console.error("[boot] the app could not be started", error)
        root.render(
            <div style={{fontFamily: "sans-serif", margin: 50}}>
                <h1>Docoon</h1>
                <p>L&apos;application n&apos;a pas pu démarrer. Rechargez la page ou contactez le support.</p>
            </div>
        )
    })
